import api from './client';

export type LegalEntity = 'autoregium' | 'fomin';

export const LEGAL_ENTITY_LABELS: Record<LegalEntity, string> = {
  autoregium: 'ООО АвтоРегиум',
  fomin: 'ИП Фомин Д.А.',
};

export interface EcologyWasteType {
  id: number;
  legal_entity: LegalEntity;
  name: string;
  description?: string | null;
  fkko_code?: string | null;
  hazard_class?: number | null;
  passport_status: string;
  passport_date?: string | null;
  passport_file_path?: string | null;
  contractor_name?: string | null;
  disposal_periodicity?: string | null;
  is_active: boolean;
  collection_instructions?: string | null;
  container_type?: string | null;
  container_location?: string | null;
  contract_document_id?: number | null;
  created_at: string;
  updated_at: string;
  disposal_count: number;
  last_disposal_date?: string | null;
}

export interface EcologyWasteTypePayload {
  legal_entity?: LegalEntity;
  name: string;
  description?: string | null;
  fkko_code?: string | null;
  hazard_class?: number | null;
  passport_status?: string;
  passport_date?: string | null;
  contractor_name?: string | null;
  disposal_periodicity?: string | null;
  is_active?: boolean;
  collection_instructions?: string | null;
  container_type?: string | null;
  container_location?: string | null;
  contract_document_id?: number | null;
}

export interface EcologyDisposalRecord {
  id: number;
  waste_type_id: number;
  legal_entity: LegalEntity;
  disposal_date: string;
  quantity: number;
  unit: string;
  cost?: number | null;
  contractor_name?: string | null;
  document_number?: string | null;
  notes?: string | null;
  created_by_id?: number | null;
  created_at: string;
}

export interface EcologyDisposalPayload {
  waste_type_id: number;
  legal_entity: LegalEntity;
  disposal_date: string;
  quantity: number;
  unit?: string;
  cost?: number | null;
  contractor_name?: string | null;
  document_number?: string | null;
  notes?: string | null;
}

export interface EcologyLicense {
  id: number;
  license_number: string;
  legal_entity: LegalEntity;
  issued_by?: string | null;
  issue_date?: string | null;
  expiry_date?: string | null;
  file_path?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  days_until_expiry?: number | null;
}

export interface EcologyLicensePayload {
  license_number: string;
  legal_entity?: LegalEntity;
  issued_by?: string | null;
  issue_date?: string | null;
  expiry_date?: string | null;
  file_path?: string | null;
  is_active?: boolean;
}

// ── Waste Types ───────────────────────────────────────────────────

export const listWasteTypes = (params: {
  legal_entity?: LegalEntity;
  is_active?: boolean;
  search?: string;
} = {}): Promise<EcologyWasteType[]> =>
  api.get<EcologyWasteType[]>('/ecology/waste-types', { params }).then(r => r.data);

export const getWasteType = (id: number): Promise<EcologyWasteType> =>
  api.get<EcologyWasteType>(`/ecology/waste-types/${id}`).then(r => r.data);

export const createWasteType = (data: EcologyWasteTypePayload): Promise<EcologyWasteType> =>
  api.post<EcologyWasteType>('/ecology/waste-types', data).then(r => r.data);

export const updateWasteType = (id: number, data: Partial<EcologyWasteTypePayload>): Promise<EcologyWasteType> =>
  api.put<EcologyWasteType>(`/ecology/waste-types/${id}`, data).then(r => r.data);

export const deleteWasteType = (id: number): Promise<void> =>
  api.delete(`/ecology/waste-types/${id}`).then(() => undefined);

// ── Disposal Records ──────────────────────────────────────────────

export const listDisposals = (wasteTypeId: number): Promise<EcologyDisposalRecord[]> =>
  api.get<EcologyDisposalRecord[]>(`/ecology/waste-types/${wasteTypeId}/disposals`).then(r => r.data);

export const createDisposal = (wasteTypeId: number, data: EcologyDisposalPayload): Promise<EcologyDisposalRecord> =>
  api.post<EcologyDisposalRecord>(`/ecology/waste-types/${wasteTypeId}/disposals`, data).then(r => r.data);

export const updateDisposal = (recordId: number, data: Partial<EcologyDisposalPayload>): Promise<EcologyDisposalRecord> =>
  api.put<EcologyDisposalRecord>(`/ecology/disposals/${recordId}`, data).then(r => r.data);

export const deleteDisposal = (recordId: number): Promise<void> =>
  api.delete(`/ecology/disposals/${recordId}`).then(() => undefined);

// ── Licenses ──────────────────────────────────────────────────────

export const listLicenses = (params: {
  legal_entity?: LegalEntity;
  is_active?: boolean;
} = {}): Promise<EcologyLicense[]> =>
  api.get<EcologyLicense[]>('/ecology/licenses', { params }).then(r => r.data);

export const createLicense = (data: EcologyLicensePayload): Promise<EcologyLicense> =>
  api.post<EcologyLicense>('/ecology/licenses', data).then(r => r.data);

export const updateLicense = (id: number, data: Partial<EcologyLicensePayload>): Promise<EcologyLicense> =>
  api.put<EcologyLicense>(`/ecology/licenses/${id}`, data).then(r => r.data);

export const deleteLicense = (id: number): Promise<void> =>
  api.delete(`/ecology/licenses/${id}`).then(() => undefined);

// ── Summary ───────────────────────────────────────────────────────

export interface DisposalSummaryItem {
  month: string;
  count: number;
  total_quantity: number;
  total_cost: number;
}

export const getDisposalSummary = (params: {
  year?: number;
  legal_entity?: LegalEntity;
} = {}): Promise<DisposalSummaryItem[]> =>
  api.get<DisposalSummaryItem[]>('/ecology/disposals/summary', { params }).then(r => r.data);

// ── Contractors ───────────────────────────────────────────────────

export type ContractorDocType = 'act' | 'report' | 'invoice' | 'other';

export const CONTRACTOR_DOC_TYPE_LABELS: Record<ContractorDocType, string> = {
  act: 'Акт',
  report: 'Отчёт',
  invoice: 'Счёт',
  other: 'Прочее',
};

export interface EcologyContractor {
  id: number;
  name: string;
  inn?: string | null;
  legal_entity: LegalEntity;
  contract_document_id?: number | null;
  contract_number?: string | null;
  contract_date?: string | null;
  contract_valid_until?: string | null;
  contact_info?: string | null;
  notes?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  documents_count: number;
  contract_status: 'none' | 'active' | 'expired';
}

export interface EcologyContractorPayload {
  name: string;
  inn?: string | null;
  legal_entity?: LegalEntity;
  contract_document_id?: number | null;
  contract_number?: string | null;
  contract_date?: string | null;
  contract_valid_until?: string | null;
  contact_info?: string | null;
  notes?: string | null;
  is_active?: boolean;
}

export interface EcologyContractorDocument {
  id: number;
  contractor_id: number;
  doc_type: ContractorDocType;
  number?: string | null;
  date?: string | null;
  period_from?: string | null;
  period_to?: string | null;
  scanned_document_id?: number | null;
  notes?: string | null;
  created_by_id?: number | null;
  created_at: string;
}

export interface EcologyContractorDocumentPayload {
  doc_type: ContractorDocType;
  number?: string | null;
  date?: string | null;
  period_from?: string | null;
  period_to?: string | null;
  scanned_document_id?: number | null;
  notes?: string | null;
}

export const listContractors = (params: {
  legal_entity?: LegalEntity;
  is_active?: boolean;
  search?: string;
} = {}): Promise<EcologyContractor[]> =>
  api.get<EcologyContractor[]>('/ecology/contractors', { params }).then(r => r.data);

export const getContractor = (id: number): Promise<EcologyContractor> =>
  api.get<EcologyContractor>(`/ecology/contractors/${id}`).then(r => r.data);

export const createContractor = (data: EcologyContractorPayload): Promise<EcologyContractor> =>
  api.post<EcologyContractor>('/ecology/contractors', data).then(r => r.data);

export const updateContractor = (id: number, data: Partial<EcologyContractorPayload>): Promise<EcologyContractor> =>
  api.put<EcologyContractor>(`/ecology/contractors/${id}`, data).then(r => r.data);

export const deleteContractor = (id: number): Promise<void> =>
  api.delete(`/ecology/contractors/${id}`).then(() => undefined);

export const listContractorDocuments = (
  contractorId: number,
  docType?: ContractorDocType,
): Promise<EcologyContractorDocument[]> =>
  api.get<EcologyContractorDocument[]>(`/ecology/contractors/${contractorId}/documents`, {
    params: docType ? { doc_type: docType } : {},
  }).then(r => r.data);

export const createContractorDocument = (
  contractorId: number,
  data: EcologyContractorDocumentPayload,
): Promise<EcologyContractorDocument> =>
  api.post<EcologyContractorDocument>(`/ecology/contractors/${contractorId}/documents`, data).then(r => r.data);

export const updateContractorDocument = (
  recordId: number,
  data: Partial<EcologyContractorDocumentPayload>,
): Promise<EcologyContractorDocument> =>
  api.put<EcologyContractorDocument>(`/ecology/contractor-documents/${recordId}`, data).then(r => r.data);

export const deleteContractorDocument = (recordId: number): Promise<void> =>
  api.delete(`/ecology/contractor-documents/${recordId}`).then(() => undefined);

// ── Contractor Appendices ─────────────────────────────────────────

export type AppendixKind = 'instruction' | 'form' | 'list' | 'other';

export const APPENDIX_KIND_LABELS: Record<AppendixKind, string> = {
  instruction: 'Инструкция',
  form: 'Форма',
  list: 'Перечень',
  other: 'Прочее',
};

export interface EcologyContractorAppendix {
  id: number;
  contractor_id: number;
  appendix_number: number;
  title: string;
  kind: AppendixKind;
  content?: string | null;
  created_at: string;
  updated_at: string;
}

export interface EcologyContractorAppendixPayload {
  appendix_number: number;
  title: string;
  kind?: AppendixKind;
  content?: string | null;
}

export const listContractorAppendices = (contractorId: number): Promise<EcologyContractorAppendix[]> =>
  api.get<EcologyContractorAppendix[]>(`/ecology/contractors/${contractorId}/appendices`).then(r => r.data);

export const createContractorAppendix = (
  contractorId: number,
  data: EcologyContractorAppendixPayload,
): Promise<EcologyContractorAppendix> =>
  api.post<EcologyContractorAppendix>(`/ecology/contractors/${contractorId}/appendices`, data).then(r => r.data);

export const updateContractorAppendix = (
  appendixId: number,
  data: Partial<EcologyContractorAppendixPayload>,
): Promise<EcologyContractorAppendix> =>
  api.put<EcologyContractorAppendix>(`/ecology/contractor-appendices/${appendixId}`, data).then(r => r.data);

export const deleteContractorAppendix = (appendixId: number): Promise<void> =>
  api.delete(`/ecology/contractor-appendices/${appendixId}`).then(() => undefined);

// ── Contractor Obligations ────────────────────────────────────────

export interface EcologyObligation {
  id: number;
  contractor_id: number;
  title: string;
  contract_clause?: string | null;
  description?: string | null;
  appendix_number?: number | null;
  periodicity: string;
  due_day?: number | null;
  notify_days_before: number;
  recipient?: string | null;
  assignee_user_id?: number | null;
  is_active: boolean;
  next_due_date?: string | null;
  days_left?: number | null;
  created_at: string;
  updated_at: string;
}

export interface EcologyObligationPayload {
  title: string;
  contract_clause?: string | null;
  description?: string | null;
  appendix_number?: number | null;
  periodicity?: string;
  due_day?: number | null;
  notify_days_before?: number;
  recipient?: string | null;
  assignee_user_id?: number | null;
  is_active?: boolean;
}

export const listContractorObligations = (contractorId: number): Promise<EcologyObligation[]> =>
  api.get<EcologyObligation[]>(`/ecology/contractors/${contractorId}/obligations`).then(r => r.data);

export const createContractorObligation = (
  contractorId: number,
  data: EcologyObligationPayload,
): Promise<EcologyObligation> =>
  api.post<EcologyObligation>(`/ecology/contractors/${contractorId}/obligations`, data).then(r => r.data);

export const updateObligation = (
  obligationId: number,
  data: Partial<EcologyObligationPayload>,
): Promise<EcologyObligation> =>
  api.put<EcologyObligation>(`/ecology/obligations/${obligationId}`, data).then(r => r.data);

export const deleteObligation = (obligationId: number): Promise<void> =>
  api.delete(`/ecology/obligations/${obligationId}`).then(() => undefined);

// ── DOCX-формы договора ───────────────────────────────────────────

const downloadDocx = async (url: string, params: Record<string, string | number>): Promise<void> => {
  const resp = await api.get(url, { params, responseType: 'blob' });
  const disposition: string = resp.headers['content-disposition'] || '';
  const m = disposition.match(/filename\*=UTF-8''([^;]+)/);
  const filename = m ? decodeURIComponent(m[1]) : 'document.docx';
  const link = document.createElement('a');
  link.href = URL.createObjectURL(resp.data);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const downloadMonthlyReport = (contractorId: number, year?: number, month?: number): Promise<void> =>
  downloadDocx(`/ecology/contractors/${contractorId}/monthly-report`, year && month ? { year, month } : {});

export const downloadTransferAct = (contractorId: number, date?: string): Promise<void> =>
  downloadDocx(`/ecology/contractors/${contractorId}/transfer-act`, date ? { date } : {});
